import { el } from './dom';
import { richText } from './ui/icons';

/**
 * The Terraboma origin legend — a 6-panel comic shown ONCE, right after a new
 * player first enters the world (i.e. just after registration). The art is
 * Higgsfield-illustrated (public/ui/legend/comic_N.webp, caption-free); the
 * caption is drawn over it in our own Terraboma pixel font so the whole thing
 * reads as part of the game, not a generic modal. Full-screen + pixel-native
 * chrome. Gated by a localStorage flag like the other prefs.
 */
const KEY = 'tb-legend-seen';

const CAPTIONS = [
  'Long ago, the valley of Terraboma withered beneath a smoking volcano.',
  'From the last ember of life, a tiny mushroom spirit was born.',
  'Wherever it wandered, the barren earth burst back into bloom.',
  'A young farmer found the little spirit — and a friendship took root.',
  'Together they raised farms, a village, and a bustling market.',
  'But the mountain still stirs… and heroes rise in the Arena to defend Terraboma.',
];
const N = CAPTIONS.length;

/**
 * How high the caption plaque sits, per slide. Panel 3's shroom stands
 * bottom-right with its cap top at ~63% of the panel height — i.e. right behind
 * the default plaque band, so the box covered its face. Only that slide is
 * lifted (to just above the cap, not up into the sky); every other slide keeps
 * the default so they stay pixel-identical.
 *
 * The lift cannot be a plain `vh`: the art is painted `background-size:cover`,
 * so the panel is scaled to cover the viewport and cropped on the over-long
 * axis, and a viewport offset drifts off the shroom's face as the aspect ratio
 * changes. So we express it in the ART's own coordinate space instead. Under
 * cover, the drawn panel is `A = max(100vh, 100vw * H / W)` tall and is centred,
 * so a point `f` of the way up from the panel's bottom edge sits at
 * `(100vh - A) / 2 + f * A` from the bottom of the viewport, which reduces to
 * `50vh - (0.5 - f) * A`. With f = 0.365 (just above the cap) that tracks the
 * face on every aspect — and it still lands on ~34vh at the 16:9 window the
 * defect was reported on. The clamp keeps it inside the screen on extremes:
 * never below the default band (which is the bug), never up in the sky.
 */
const PANEL_W = 1600;
const PANEL_H = 1073;
/** Height of the cover-scaled panel, in viewport units. */
const ART_H = `max(100vh, ${((PANEL_H / PANEL_W) * 100).toFixed(4)}vw)`;
/** `bottom` for a plaque anchored at fraction `f` of the panel, up from its bottom edge. */
const artBottom = (f: number, lo: string, hi: string): string =>
  `clamp(${lo}, calc(50vh - ${(0.5 - f).toFixed(3)} * ${ART_H}), ${hi})`;

const CAPTION_BOTTOM_DEFAULT = '15vh';
export const CAPTION_BOTTOM: readonly (string | undefined)[] = [
  undefined,
  undefined,
  artBottom(0.365, CAPTION_BOTTOM_DEFAULT, '55vh'),
  undefined,
  undefined,
  undefined,
];
/** Plaque `bottom` for slide `i`, falling back to the shared default. */
export const captionBottom = (i: number): string => CAPTION_BOTTOM[i] ?? CAPTION_BOTTOM_DEFAULT;

export function legendSeen(): boolean {
  try { return localStorage.getItem(KEY) === '1'; } catch { return true; }
}
function markSeen(): void { try { localStorage.setItem(KEY, '1'); } catch { /* private mode */ } }

/** Show the legend over everything; resolves when the player dismisses it. */
export function showLegend(): Promise<void> {
  return new Promise((resolve) => {
    let i = 0;

    // Full-bleed art fills the screen; the illustration sits behind a pixel
    // caption plaque + chrome.
    const art = el('div', 'absolute inset-0', {
      style: 'background-size:cover;background-position:center;image-rendering:auto;transition:opacity .18s',
    });

    // The game speaks in ONE face. The serif here dated from the pack font, which
    // was unreadable at paragraph size; Terraboma is not, so the intro no longer
    // opts out. Whole-pixel sizes only (the face is drawn on a fixed grid, and a
    // clamp() landing on 21.4px interpolates every stem) and a loose line-height,
    // which is what pixel type actually needs at paragraph length.
    const READ_FONT = '"Terraboma", "CuteFantasy", serif';
    const caption = el('div', '', {
      style: `font-family:${READ_FONT};color:#f7e8cd;font-size:20px;`
        + 'line-height:1.8;text-align:center;text-shadow:0 2px 6px rgba(0,0,0,.9)',
    });
    const plaque = el('div', 'tb-legend-plaque absolute left-1/2 -translate-x-1/2', {
      style: `bottom:var(--tb-cap-bottom,${CAPTION_BOTTOM_DEFAULT});width:min(880px,90vw);padding:18px 26px;background:rgba(22,15,10,.9);`
        + 'border:3px solid #7a5c3a;border-radius:10px;box-shadow:0 0 0 3px rgba(0,0,0,.5),0 8px 30px rgba(0,0,0,.6)',
    }, caption);

    // Title banner — legible serif display face.
    const title = el('div', 'absolute left-1/2 -translate-x-1/2', {
      style: `top:5vh;font-family:${READ_FONT};font-weight:700;color:#ffd75e;`
        // 32px is a whole multiple of the face's design grid; letter-spacing stays
        // at 0 because Terraboma carries its own one-pixel advance.
        + 'font-size:32px;text-shadow:0 3px 8px rgba(0,0,0,.9);letter-spacing:0',
    }, 'The Legend of Terraboma');

    // Progress dots — live INSIDE the single bottom control row (below), so the
    // Back/Next buttons can never overlap them.
    const dots = el('div', 'flex items-center gap-2');

    const back = el('button', 'tb-pixel-btn px-5 py-2.5', { style: 'font-size:15px' }, '‹ Back') as HTMLButtonElement;
    const next = el('button', 'tb-pixel-btn px-6 py-2.5', { style: 'font-size:15px' }, 'Next ›') as HTMLButtonElement;
    // One centered bar: [Back]  • • • • • •  [Next] — no overlap, evenly spaced.
    const nav = el('div', 'absolute left-1/2 -translate-x-1/2 flex items-center gap-5 px-4 py-2 rounded-full', {
      style: 'bottom:4vh;background:rgba(20,13,8,.72);border:2px solid #4a3826',
    }, back, dots, next);

    const skip = el('button', 'tb-close-x tb-pixel-btn absolute px-3 py-1.5', {
      style: 'top:5vh;right:4vw;font-size:12px', title: 'Skip the intro',
    }, ...richText('Skip ✕')) as HTMLButtonElement;

    // WebP, not PNG: the six panels were 5-8 MB PNGs each — 35 MB, over half the
    // whole public payload, for a cinematic shown once. Re-encoded they are ~1 MB
    // total (frontend/scripts/build-legend-webp.py).
    const panelUrl = (n: number) => `/ui/legend/comic_${n}.webp`;

    // Warm the NEXT panel while this one is on screen, so pressing Next never
    // shows an empty frame while the image fetches.
    const prefetch = (n: number) => {
      if (n < 1 || n > N) return;
      const img = new Image();
      img.decoding = 'async';
      img.src = panelUrl(n);
    };

    const paint = () => {
      art.style.backgroundImage = `url(${panelUrl(i + 1)})`;
      prefetch(i + 2);
      caption.textContent = CAPTIONS[i];
      // Through a custom property: the value is a clamp()/calc() the plain
      // `bottom` longhand would not survive in every engine's inline parser.
      plaque.style.setProperty('--tb-cap-bottom', captionBottom(i));
      back.style.visibility = i === 0 ? 'hidden' : 'visible';
      next.textContent = i === N - 1 ? 'Enter Terraboma' : 'Next ›';
      dots.replaceChildren(...Array.from({ length: N }, (_, k) =>
        el('span', '', { style: `width:11px;height:11px;border-radius:50%;`
          + `background:${k === i ? '#ffd75e' : '#5a4632'};border:2px solid #2a1d12;box-shadow:0 1px 0 #000` })));
    };

    const done = () => { markSeen(); overlay.remove(); window.removeEventListener('keydown', onKey); resolve(); };
    const go = (d: number) => { i = Math.min(N - 1, Math.max(0, i + d)); paint(); };
    next.onclick = () => (i === N - 1 ? done() : go(1));
    back.onclick = () => go(-1);
    skip.onclick = done;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') next.click();
      else if (e.key === 'ArrowLeft') back.click();
      else if (e.key === 'Escape') done();
    };
    window.addEventListener('keydown', onKey);

    // A soft dark vignette so the art edges blend into the screen (no hard card).
    const vignette = el('div', 'absolute inset-0 pointer-events-none', {
      style: 'box-shadow:inset 0 0 180px 60px rgba(10,7,4,.85)',
    });
    const overlay = el('div', 'fixed inset-0 z-[80]', { style: 'background:#0e0a06' },
      art, vignette, title, plaque, dots, nav, skip);
    document.body.appendChild(overlay);
    paint();
  });
}

/** Show the legend once, on first entry. No-op if already seen. */
export async function maybeShowLegend(): Promise<void> {
  if (!legendSeen()) await showLegend();
}
